import { z } from 'zod';

/**
 * Request bodies for the bid-placement endpoints (`docs/04-api-contract.md`).
 * `POST .../bids` (a normal +1-increment bid) takes no body. The two below cover the
 * early-bid and free-bid variants.
 */

/**
 * Upper bound on increment steps resolved in one call (early-bid `times`, and mirrored by
 * the DO's jump cap). Bounds the synchronous walk so a huge count can't exhaust the
 * per-sale Durable Object's CPU budget and block all bidding/alarm work for that sale.
 */
export const MAX_BID_STEPS = 10_000;

/**
 * `POST .../bids` — the one-tap +1 bid. Body optional.
 *
 * `expectedAmount` is the figure the bidder was **shown** when they confirmed. The server
 * resolves the real amount from the *current* top bid, so between rendering a confirmation and
 * the request arriving another bidder can raise the price — and without this the bidder would
 * commit a larger binding bid than the one they agreed to. Supplying it turns that into a
 * rejection the client can re-render, rather than a silent difference.
 *
 * Optional so an older client keeps working, and so the jump path (which already names a
 * ceiling) and the early-bid path (which shows no total) need not use it.
 */
export const placeBidSchema = z.object({
  expectedAmount: z.number().int().positive().optional(), // kuruş
});
export type PlaceBidBody = z.infer<typeof placeBidSchema>;

/** `POST .../bids/early` — place `times` increments in one call (GOLDBIDDER / deposit). */
export const earlyBidSchema = z.object({
  times: z.number().int().positive().max(MAX_BID_STEPS),
});
export type EarlyBidInput = z.infer<typeof earlyBidSchema>;

/** `POST .../bids/free` — admin sets an explicit amount in kuruş (ADMIN / DEVELOPER). */
export const freeBidSchema = z.object({
  bid: z.number().int().positive(), // kuruş
});
export type FreeBidInput = z.infer<typeof freeBidSchema>;

/**
 * `POST .../bids/jump` — manual bid-jump (M4.5). The bidder types a target amount; the
 * server resolves it to the intermediate increment steps and commits **one** bid at the
 * largest increment-aligned amount ≤ `target` (must clear the current top by ≥1 step).
 * Persisted as an ordinary `kind: 'bid'` — "jump" is only the resolution strategy. Below-
 * band / arbitrary amounts remain the ADMIN free-bid path.
 */
export const bidJumpSchema = z.object({
  target: z.number().int().positive(), // kuruş
});
export type BidJumpInput = z.infer<typeof bidJumpSchema>;

/**
 * `POST /admin/sales/:id/lots/:lotId/green` — admin sets/adjusts the lot's **hidden
 * reserve** (in kuruş; never shown to bidders). Once a bid crosses it the lot auto-flips
 * to guaranteed sale and `bid.placed` events carry `priceGoalReached: true` (ADR 0013).
 */
export const greenLightSchema = z.object({
  reserve: z.number().int().positive(), // kuruş
});
export type GreenLightInput = z.infer<typeof greenLightSchema>;

/**
 * Header carrying a caller-generated idempotency key on the four **bid-placement** endpoints
 * (M19): `POST .../bids`, `.../bids/early`, `.../bids/jump` and `.../bids/free`. Not the
 * admin reserve endpoint (`.../green`), which places no bid — M19's finding is about a
 * *bid* committing twice.
 *
 * The `SaleDO` critical section already makes bid placement **serializable** (ADR 0007) — two
 * concurrent bids can never read the same stale top. What it does not make it is **retry-safe**:
 * a request whose response was lost still committed, so a client that resends it (a timed-out
 * fetch the operator clicks through again, an intermediary replay) would commit a second bid
 * against the same intent. The key closes that gap by letting the DO recognise the resend.
 *
 * Deliberately a header, not a body field: `POST .../bids` takes no body at all, so a body
 * field would make one endpoint's contract differ from the other three.
 */
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

/**
 * A bid idempotency key. Opaque to the server — it only ever compares it — so the shape is
 * bounded rather than prescribed: long enough that a UUID fits, short enough that it cannot be
 * used to push arbitrary data into the DO's key map. Clients generate one `crypto.randomUUID()`
 * per user gesture and reuse it for every retry of that gesture.
 */
export const idempotencyKeySchema = z
  .string()
  .min(8)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/);
